import {
  filterPresentRoster,
  isSessionPresentFlag,
  sessionPresencePlayerKey,
} from "./coachSessionTables";

function splitPlayerName(fullName) {
  const trimmed = String(fullName || "").trim();
  if (!trimmed) return { first: null, last: null };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: null };
  // FFTT-style "POLAR Ricardo" / "BENITO OLMOS Victor" — do not invent swapped
  // first/last (that created a second tile "Ricardo POLAR" next to "POLAR Ricardo").
  if (looksLikeLastFirstRosterName(trimmed)) {
    return { first: null, last: null };
  }
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

/** True when the first token is ALL CAPS (club roster / FFTT last-name-first). */
function looksLikeLastFirstRosterName(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length < 2) return false;
  const head = parts[0];
  const tail = parts[parts.length - 1];
  const headAllCaps = /^[A-ZÀ-ÖØ-Þ]{2,}(?:[-'][A-ZÀ-ÖØ-Þ]+)*$/.test(head);
  const tailHasLower = /[a-zà-öø-ÿ]/.test(tail);
  return headAllCaps && tailHasLower;
}

/** Order-invariant name key so "POLAR Ricardo" and "Ricardo POLAR" match. */
export const playerNameFingerprint = (player) => {
  const raw =
    getPlayerDisplayName(player) ||
    humanLabel(player?.player_name) ||
    [player?.user_first_name, player?.user_last_name].filter(Boolean).join(" ");
  const tokens = String(raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .sort();
  return tokens.length ? tokens.join(" ") : "";
};

function preferHumanNamePart(preferred, fallback) {
  const a = humanLabel(preferred);
  const b = humanLabel(fallback);
  if (a && !/^[A-ZÀ-ÖØ-Þ\s'-]+$/.test(a)) return a;
  if (b && !/^[A-ZÀ-ÖØ-Þ\s'-]+$/.test(b)) return b;
  return a || b || null;
}

/** True for opaque SmartTT ids (pl… / us…) — never show these as labels. */
export const isOpaqueEntityId = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return false;
  return /^(pl|us|te|ts|gr|cl)\d+$/i.test(s);
};

const humanLabel = (value) => {
  const s = String(value ?? "").trim();
  if (!s || isOpaqueEntityId(s)) return "";
  return s;
};

export const getPlayerDisplayName = (player) => {
  if (!player) return "";
  const fromParts = [player.user_first_name, player.user_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const fromGrpList = [player.firstName, player.lastName].filter(Boolean).join(" ").trim();
  return (
    humanLabel(player.user_alias) ||
    humanLabel(fromParts) ||
    humanLabel(player.player_name) ||
    humanLabel(player.user_name) ||
    humanLabel(player.name) ||
    humanLabel(fromGrpList) ||
    ""
  );
};

export const resolvePlayerKey = (player) => {
  if (!player) return null;
  if (player._filterKey) return String(player._filterKey);
  if (player.player_id != null && String(player.player_id).trim() !== "") {
    return String(player.player_id);
  }
  if (player.user_id != null && String(player.user_id).trim() !== "") {
    return String(player.user_id);
  }
  if (player.id != null && String(player.id).trim() !== "") {
    return String(player.id);
  }
  const name = getPlayerDisplayName(player).toLowerCase().trim();
  return name ? `name:${name}` : null;
};

export const isAssignedTableSlot = (slot) =>
  slot != null &&
  typeof slot === "object" &&
  Object.keys(slot).length > 0 &&
  (slot.player_id ||
    slot.user_id ||
    slot.id ||
    slot.user_first_name ||
    slot.user_alias ||
    slot.user_name ||
    slot.player_name ||
    slot.name);

export const normalizeSessionPlayer = (player) => {
  if (!player || typeof player !== "object") return null;
  const nestedPlayer = player.tbl_player || player.player || null;
  const nestedUser = player.tbl_user || nestedPlayer?.tbl_user || null;
  const playerName =
    player.player_name ?? nestedUser?.player_name ?? nestedPlayer?.player_name ?? null;
  const splitName = splitPlayerName(playerName);
  return {
    player_id: player.player_id ?? nestedPlayer?.player_id ?? null,
    user_id:
      player.user_id ??
      player.id ??
      nestedPlayer?.user_id ??
      nestedUser?.user_id ??
      null,
    user_alias: player.user_alias ?? nestedUser?.user_alias ?? nestedPlayer?.user_alias ?? null,
    player_name: playerName,
    user_first_name:
      player.user_first_name ??
      player.firstName ??
      player.first_name ??
      nestedUser?.user_first_name ??
      splitName.first ??
      player.user_name ??
      player.name ??
      null,
    user_last_name:
      player.user_last_name ??
      player.lastName ??
      player.last_name ??
      nestedUser?.user_last_name ??
      splitName.last ??
      null,
    user_sex:
      player.user_sex ??
      player.gender ??
      player.sex ??
      nestedUser?.user_sex ??
      null,
    gender:
      player.gender ??
      player.user_sex ??
      player.sex ??
      nestedUser?.user_sex ??
      null,
    sex:
      player.sex ??
      player.user_sex ??
      player.gender ??
      nestedUser?.user_sex ??
      null,
    user_date_birth:
      player.user_date_birth ??
      player.birth_date ??
      player.date_birth ??
      player.dateOfBirth ??
      nestedUser?.user_date_birth ??
      null,
    birth_date:
      player.birth_date ??
      player.user_date_birth ??
      player.date_birth ??
      player.dateOfBirth ??
      nestedUser?.user_date_birth ??
      null,
    date_birth:
      player.date_birth ??
      player.user_date_birth ??
      player.birth_date ??
      nestedUser?.user_date_birth ??
      null,
    dateOfBirth:
      player.dateOfBirth ??
      player.user_date_birth ??
      player.birth_date ??
      nestedUser?.user_date_birth ??
      null,
    age: player.age ?? player.player_age ?? null,
    player_age: player.player_age ?? player.age ?? null,
    user_weight:
      player.user_weight ??
      player.weight ??
      nestedUser?.user_weight ??
      null,
    weight: player.weight ?? player.user_weight ?? nestedUser?.user_weight ?? null,
    fltt_classement:
      player.fltt_classement ??
      player.classement ??
      nestedPlayer?.fltt_classement ??
      null,
    fltt_ranking_points:
      player.fltt_ranking_points ??
      player.ranking_points ??
      nestedPlayer?.fltt_ranking_points ??
      null,
    classement:
      player.classement ??
      player.fltt_classement ??
      nestedPlayer?.fltt_classement ??
      null,
    ranking_points:
      player.ranking_points ??
      player.fltt_ranking_points ??
      nestedPlayer?.fltt_ranking_points ??
      null,
    auto_evaluation_data: player.auto_evaluation_data ?? null,
    // Keep presence so seating editors (incl. finalized correction) can treat
    // session participants as seatable after roster normalization.
    presensee:
      player.presensee ??
      player.isPresent ??
      player.is_present ??
      nestedPlayer?.presensee ??
      null,
    isPresent:
      player.isPresent ??
      player.presensee ??
      player.is_present ??
      nestedPlayer?.isPresent ??
      null,
  };
};

export const collectTablePlayers = (tables) => {
  const players = [];
  if (!Array.isArray(tables)) return players;

  tables.forEach((table) => {
    if (Array.isArray(table?.players)) {
      table.players.forEach((p) => {
        if (isAssignedTableSlot(p)) players.push(p);
      });
    }
    if (isAssignedTableSlot(table?.player1)) players.push(table.player1);
    if (isAssignedTableSlot(table?.player2)) players.push(table.player2);
    if (isAssignedTableSlot(table?.player3)) players.push(table.player3);
  });

  return players;
};

export const buildPlayerIndexes = (...sources) => {
  const flat = sources.flat();
  const rawSources =
    flat.length > 0 &&
    flat.every((entry) => entry != null && typeof entry === "object" && !Array.isArray(entry))
      ? flat
      : flat.filter((group) => Array.isArray(group)).flat();

  const byFilterKey = new Map();

  const upsert = (raw) => {
    const incoming = normalizeSessionPlayer(raw);
    if (!incoming) return;

    const filterKey = resolvePlayerKey(incoming);
    if (!filterKey) return;

    // Match across player_id / user_id so a presence row and a table seat
    // for the same person collapse even when each source only has one id.
    const existing =
      byFilterKey.get(filterKey) ||
      (incoming.player_id != null
        ? byFilterKey.get(String(incoming.player_id))
        : null) ||
      (incoming.user_id != null
        ? byFilterKey.get(String(incoming.user_id))
        : null) ||
      (incoming.id != null ? byFilterKey.get(String(incoming.id)) : null) ||
      null;

    const stableKey =
      (existing && (existing._filterKey || resolvePlayerKey(existing))) ||
      filterKey;

    // Prefer non-null fields: id-only table seats must not wipe names from
    // presence / exdetails rows for the same player_id.
    const merged = {
      ...existing,
      ...incoming,
      player_id: incoming.player_id ?? existing?.player_id ?? null,
      user_id: incoming.user_id ?? existing?.user_id ?? null,
      user_alias: incoming.user_alias ?? existing?.user_alias ?? null,
      player_name: incoming.player_name ?? existing?.player_name ?? null,
      user_first_name: preferHumanNamePart(
        incoming.user_first_name,
        existing?.user_first_name
      ),
      user_last_name: preferHumanNamePart(
        incoming.user_last_name,
        existing?.user_last_name
      ),
      user_sex: incoming.user_sex ?? existing?.user_sex ?? null,
      gender: incoming.gender ?? existing?.gender ?? incoming.user_sex ?? existing?.user_sex ?? null,
      sex: incoming.sex ?? existing?.sex ?? incoming.user_sex ?? existing?.user_sex ?? null,
      user_date_birth:
        incoming.user_date_birth ?? existing?.user_date_birth ?? null,
      birth_date: incoming.birth_date ?? existing?.birth_date ?? incoming.user_date_birth ?? existing?.user_date_birth ?? null,
      date_birth: incoming.date_birth ?? existing?.date_birth ?? null,
      dateOfBirth: incoming.dateOfBirth ?? existing?.dateOfBirth ?? null,
      age: incoming.age ?? existing?.age ?? null,
      player_age: incoming.player_age ?? existing?.player_age ?? null,
      user_weight: incoming.user_weight ?? existing?.user_weight ?? null,
      weight: incoming.weight ?? existing?.weight ?? null,
      fltt_classement: incoming.fltt_classement ?? existing?.fltt_classement ?? null,
      fltt_ranking_points:
        incoming.fltt_ranking_points ?? existing?.fltt_ranking_points ?? null,
      classement: incoming.classement ?? existing?.classement ?? null,
      ranking_points: incoming.ranking_points ?? existing?.ranking_points ?? null,
      auto_evaluation_data:
        incoming.auto_evaluation_data ?? existing?.auto_evaluation_data ?? null,
      presensee:
        incoming.presensee ??
        existing?.presensee ??
        incoming.isPresent ??
        existing?.isPresent ??
        null,
      isPresent:
        incoming.isPresent ??
        existing?.isPresent ??
        incoming.presensee ??
        existing?.presensee ??
        null,
      _filterKey: stableKey,
    };

    byFilterKey.set(stableKey, merged);
    byFilterKey.set(filterKey, merged);
    if (merged.player_id != null) byFilterKey.set(String(merged.player_id), merged);
    if (merged.user_id != null) byFilterKey.set(String(merged.user_id), merged);
    if (merged.id != null) byFilterKey.set(String(merged.id), merged);
  };

  rawSources.forEach(upsert);

  const unique = new Map();
  for (const player of byFilterKey.values()) {
    const dedupeKey = player._filterKey || resolvePlayerKey(player);
    if (!dedupeKey || unique.has(dedupeKey)) continue;
    unique.set(dedupeKey, player);
  }

  // Collapse "POLAR Ricardo" vs "Ricardo POLAR" when ids never linked.
  const byName = new Map();
  for (const player of unique.values()) {
    const fp = playerNameFingerprint(player);
    if (!fp) {
      byName.set(`id:${player._filterKey || resolvePlayerKey(player)}`, player);
      continue;
    }
    const existing = byName.get(fp);
    if (!existing) {
      byName.set(fp, player);
      continue;
    }
    byName.set(fp, {
      ...existing,
      ...player,
      player_id: player.player_id ?? existing.player_id ?? null,
      user_id: player.user_id ?? existing.user_id ?? null,
      user_alias: player.user_alias ?? existing.user_alias ?? null,
      player_name: player.player_name ?? existing.player_name ?? null,
      user_first_name: preferHumanNamePart(
        player.user_first_name,
        existing.user_first_name
      ),
      user_last_name: preferHumanNamePart(
        player.user_last_name,
        existing.user_last_name
      ),
      auto_evaluation_data:
        player.auto_evaluation_data ?? existing.auto_evaluation_data ?? null,
      _filterKey: existing._filterKey || player._filterKey,
    });
  }

  const list = Array.from(byName.values()).sort((a, b) =>
    getPlayerDisplayName(a).localeCompare(getPlayerDisplayName(b), undefined, {
      sensitivity: "base",
    })
  );

  const byKey = new Map();
  list.forEach((player) => {
    const key = player._filterKey || resolvePlayerKey(player);
    if (key) byKey.set(String(key), player);
    if (player.player_id != null) byKey.set(String(player.player_id), player);
    if (player.user_id != null) byKey.set(String(player.user_id), player);
    if (player.id != null) byKey.set(String(player.id), player);
  });

  return { byKey, list };
};

export const readGrIdFromStorage = () => {
  try {
    const grp = JSON.parse(localStorage.getItem("grpinf") || "null");
    const sess = JSON.parse(localStorage.getItem("rsessn") || "null");
    return (
      grp?.groupDetails?.gr_id ||
      grp?.gr_id ||
      sess?.gr_id ||
      sess?._groupId ||
      null
    );
  } catch {
    return null;
  }
};

export const resolveTableGrId = (session, resolvedGrId, selectedgrp) =>
  resolvedGrId ||
  session?.gr_id ||
  session?._groupId ||
  session?.groupDetails?.gr_id ||
  session?.tbl_group?.gr_id ||
  selectedgrp?.groupDetails?.gr_id ||
  readGrIdFromStorage() ||
  "";

export const extractMobilePlayers = (res) => {
  const payload = res?.data?.data;
  if (Array.isArray(payload?.players)) return payload.players;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const extractGroupDetailsPlayers = (res) => {
  const data = res?.data?.data;
  if (!data || typeof data !== "object") return [];
  const players = data.tbl_players || data.players || [];
  return Array.isArray(players) ? players : [];
};

const pushUniqueClubId = (ids, seen, value) => {
  if (value == null || value === "") return;
  const key = String(value).trim();
  if (!key || seen.has(key)) return;
  seen.add(key);
  ids.push(key);
};

/** Collect club_id candidates for mobile sessionDetails (requires club_id + ts_id). */
export const resolveClubIdCandidates = (user, session, selectedgrp, extra = []) => {
  const ids = [];
  const seen = new Set();
  for (const value of extra) pushUniqueClubId(ids, seen, value);
  pushUniqueClubId(ids, seen, selectedgrp?.groupDetails?.club_id);
  pushUniqueClubId(ids, seen, session?.club_id);
  pushUniqueClubId(ids, seen, session?.tbl_group?.club_id);
  pushUniqueClubId(ids, seen, user?.club_details?.club_id);
  pushUniqueClubId(ids, seen, user?.club_id);
  if (Array.isArray(user?.clubs)) {
    user.clubs.forEach((c) => pushUniqueClubId(ids, seen, c?.club_id ?? c?.id));
  }
  return ids;
};

/** Keys for players assigned to a table in at least one session exercise. */
export const collectAssignedParticipantKeys = (tablePlayers) => {
  const playerIds = new Set();
  const userIds = new Set();
  (tablePlayers || []).forEach((raw) => {
    const p = normalizeSessionPlayer(raw);
    if (!p) return;
    const pid = sessionPresencePlayerKey(p.player_id);
    const uid = sessionPresencePlayerKey(p.user_id);
    if (pid) playerIds.add(pid);
    if (uid) userIds.add(uid);
  });
  return { playerIds, userIds };
};

export const collectAutoEvalParticipantKeys = (autoEvalPlayers) => {
  const playerIds = new Set();
  const userIds = new Set();
  (autoEvalPlayers || []).forEach((raw) => {
    if (!raw?.auto_evaluation_data) return;
    const p = normalizeSessionPlayer(raw);
    if (!p) return;
    const pid = sessionPresencePlayerKey(p.player_id);
    const uid = sessionPresencePlayerKey(p.user_id);
    if (pid) playerIds.add(pid);
    if (uid) userIds.add(uid);
  });
  return { playerIds, userIds };
};

const matchesParticipantKeys = (raw, playerIds, userIds) => {
  const p = normalizeSessionPlayer(raw);
  if (!p) return false;
  const pid = sessionPresencePlayerKey(p.player_id);
  const uid = sessionPresencePlayerKey(p.user_id);
  return (pid && playerIds.has(pid)) || (uid && userIds.has(uid));
};

/**
 * Session participants: people marked present, plus anyone seated or with a
 * real auto-eval. A player-scoped exdetails row (self only, no eval) must not
 * hide the rest of the present roster.
 */
export const filterToSessionParticipants = (
  candidates,
  { tablePlayers = [], autoEvalPlayers = [], presenceRoster = [] } = {}
) => {
  const tableKeys = collectAssignedParticipantKeys(tablePlayers);
  const autoKeys = collectAutoEvalParticipantKeys(autoEvalPlayers);
  const presentRoster = filterPresentRoster(presenceRoster);
  const presentKeys = collectAssignedParticipantKeys(presentRoster);
  const participantPlayerIds = new Set([
    ...tableKeys.playerIds,
    ...autoKeys.playerIds,
    ...presentKeys.playerIds,
  ]);
  const participantUserIds = new Set([
    ...tableKeys.userIds,
    ...autoKeys.userIds,
    ...presentKeys.userIds,
  ]);

  const collected = [];

  const tryCollect = (raw) => {
    if (!raw || typeof raw !== "object") return;
    const include =
      matchesParticipantKeys(raw, participantPlayerIds, participantUserIds) ||
      isSessionPresentFlag(raw.presensee) ||
      isSessionPresentFlag(raw.isPresent);

    if (!include) return;
    collected.push(raw);
  };

  // Collect every matching source, then merge by key so name-rich rows
  // (presence / exdetails) fill fields that id-only table seats lack.
  presentRoster.forEach(tryCollect);
  tablePlayers.forEach(tryCollect);
  autoEvalPlayers.forEach(tryCollect);
  candidates.forEach(tryCollect);

  return buildPlayerIndexes(collected).list;
};

/**
 * Load players who participated in this training session (presence, tables, auto-eval).
 * Does not include the full group roster — only session performers.
 */
export async function fetchCoachSessionRoster({
  tsId,
  grId: initialGrId,
  clubCandidates,
  sessionexlist,
  tablelist,
  mobileSessionDetails,
  mobileGroupPlayers,
  grpplayerlist,
  grpuserlist,
  groupdetails,
  exdetails,
}) {
  let grId = initialGrId ? String(initialGrId).trim() : null;
  const clubIds = [...clubCandidates];
  const tablePlayers = [];
  const autoEvalPlayers = [];
  const presenceRoster = [];
  const enrichCandidates = [];

  const exRes = await sessionexlist({ ts_id: tsId }).catch(() => null);
  const exList = Array.isArray(exRes?.data?.data) ? exRes.data.data : [];

  if (grId && groupdetails) {
    try {
      const gdRes = await groupdetails(grId);
      const clubFromGroup = gdRes?.data?.data?.club_id;
      if (clubFromGroup) {
        const seen = new Set(clubIds);
        pushUniqueClubId(clubIds, seen, clubFromGroup);
      }
    } catch {
      /* club_id lookup only */
    }
  }

  if (clubIds.length && mobileSessionDetails) {
    for (const club_id of clubIds) {
      try {
        const res = await mobileSessionDetails({
          club_id,
          ts_id: tsId,
          ...(grId ? { gr_id: grId } : {}),
        });
        if (res?.data?.data) {
          const details = res.data.data;
          if (details.gr_id) grId = String(details.gr_id);
          tablePlayers.push(...collectTablePlayers(details.tables || []));
          break;
        }
      } catch {
        /* try next club */
      }
    }
  }

  const tableResults =
    exList.length > 0
      ? await Promise.all(
          exList.map((ex) =>
            tablelist({
              ts_id: tsId,
              te_id: ex.te_id,
              ...(grId ? { gr_id: grId } : {}),
            }).catch(() => null)
          )
        )
      : [];

  tableResults.forEach((res) => {
    const tableData = Array.isArray(res?.data?.data) ? res.data.data : [];
    tablePlayers.push(...collectTablePlayers(tableData));
  });

  if (grId) {
    const [mobileRes, adminRes, grpUsersRes] = await Promise.all([
      mobileGroupPlayers({ gr_id: grId, ts_id: tsId }).catch(() => null),
      grpplayerlist({ gr_id: grId, ts_id: tsId }).catch(() => null),
      grpuserlist({ gr_id: grId, ts_id: tsId }).catch(() => null),
    ]);

    if (mobileRes) presenceRoster.push(...extractMobilePlayers(mobileRes));
    if (adminRes && Array.isArray(adminRes?.data?.data)) {
      presenceRoster.push(...adminRes.data.data);
    }
    if (grpUsersRes) {
      const users = (grpUsersRes?.data?.data || []).filter(
        (p) => !p.role || String(p.role).toLowerCase() === "player"
      );
      presenceRoster.push(...users);
    }
  }

  if (exdetails && exList.length > 0) {
    const detailResults = await Promise.all(
      exList.map((ex) => exdetails({ te_id: ex.te_id }).catch(() => null))
    );
    detailResults.forEach((res) => {
      const details = Array.isArray(res?.data?.data?.player_details)
        ? res.data.data.player_details
        : [];
      autoEvalPlayers.push(...details);
      enrichCandidates.push(...details);
    });
  }

  enrichCandidates.push(...presenceRoster);

  const rosterRaw = filterToSessionParticipants(enrichCandidates, {
    tablePlayers,
    autoEvalPlayers,
    presenceRoster,
  });

  return { rosterRaw, grId, exList };
}
